/**
 * Charger-UX session reads: the canonical snapshot detail, "viewed"
 * acknowledgement and the in-app notices.
 *
 * These endpoints exist only while the API's charger-UX mode is on. When it
 * is off they answer 404 CHARGER_UX_DISABLED, which callers treat as "this
 * feature is not available" — never as an error to show.
 */
import axios from 'axios';
import { authClient } from '../auth/authClient.js';
import { isCanonicalSnapshot } from '../chargerUx/canonical';
import { applySnapshot } from '../chargerUx/canonicalStore';

const SESSIONS_BASE = `${import.meta.env.VITE_API_URL}/ev/sessions`;
const SESSION_UID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const failure = (err) => {
  const status = err?.response?.status ?? null;
  const code = err?.response?.data?.error ?? null;
  return {
    ok: false,
    status,
    // The API is not offering canonical state: render the legacy experience.
    unavailable: status === 404 && code === 'CHARGER_UX_DISABLED',
    // No such session for this caller, or access was revoked.
    denied: status === 401 || status === 403 || (status === 404 && code !== 'CHARGER_UX_DISABLED'),
    error: code || 'CANONICAL_LOOKUP_FAILED',
  };
};

/**
 * The session's current canonical state, simplified timeline and notices.
 * Always fetched fresh: this is what a notification opens, and a notification
 * may be older than the state it described.
 */
export async function getCanonicalSessionDetail(sessionUid) {
  if (typeof sessionUid !== 'string' || !SESSION_UID_RE.test(sessionUid)) {
    return { ok: false, status: 400, unavailable: false, denied: true, error: 'INVALID_SESSION_UID' };
  }
  try {
    const res = await axios.get(`${SESSIONS_BASE}/${encodeURIComponent(sessionUid)}/canonical`);
    const body = res.data || {};
    const canonical = isCanonicalSnapshot(body.canonical) ? body.canonical : null;
    if (canonical) applySnapshot(canonical);
    return {
      ok: true,
      canonical,
      timeline: Array.isArray(body.timeline) ? body.timeline : [],
      notices: Array.isArray(body.notices) ? body.notices : [],
    };
  } catch (err) {
    return failure(err);
  }
}

/** Tell the API the client has seen the session state up to `version`. Best effort. */
export async function markCanonicalViewed(sessionUid, version) {
  if (typeof sessionUid !== 'string' || !SESSION_UID_RE.test(sessionUid)) return { ok: false };
  try {
    const res = await axios.post(`${SESSIONS_BASE}/${encodeURIComponent(sessionUid)}/canonical/viewed`, { version });
    return { ok: true, viewed: res.data?.viewed ?? 0 };
  } catch (err) {
    return failure(err);
  }
}

/** In-app notices for the signed-in user's sessions. Empty when the feature is off. */
export async function listSessionNotifications() {
  try {
    const res = await authClient.get('/user/sessions/notifications');
    const list = Array.isArray(res.data?.notifications) ? res.data.notifications : [];
    return { ok: true, notifications: list };
  } catch (err) {
    const f = failure(err);
    return f.unavailable ? { ok: true, notifications: [], unavailable: true } : f;
  }
}
